import React from "react"
import { FormattedMessage, IntlShape, useIntl } from "react-intl"
import { formatNumber } from "helpers/numbers"
import { concatClassNames } from "helpers/jsxHelpers"
import { mapDates, mapGrowth } from "./constants"
import "./index.scss"
import {
	DETAIL_ID,
	getDetailTrack,
	getLegend,
	getSymbol,
	GROWTH_DATES_ID,
	isGrowth,
	METRIC_ID,
} from "./utils"

interface TooltipProps {
	active?: boolean
	payload?: any[]
	primaryMetric: string
	secondaryMetric: string
	template: string
}

const ColorLine: React.FC<{
	color: string
	strokeDasharray?: string
}> = ({ color, strokeDasharray }) => (
	<svg height="1rem" width="0.25rem">
		<line
			x1="0"
			y1="0"
			x2="0"
			y2="1rem"
			strokeDasharray={strokeDasharray}
			stroke={color}
			strokeWidth="100%"
		/>
	</svg>
)

const FormattedPercentage: React.FC<{
	detail?: number
	symbol?: string
	intl: IntlShape
}> = ({ detail, symbol = "", intl }) => {
	if (detail === undefined) return null
	return (
		<span className={concatClassNames([detail > 0 ? "positive" : "negative"])}>
			<FormattedMessage
				id={DETAIL_ID}
				values={{
					symbol,
					amount: formatNumber(detail, intl),
					b: (text: string) => <b>({text})</b>,
				}}
			/>
		</span>
	)
}

const GrowthDates: React.FC<{
	template: string
	primaryDate: string
	secondaryDate: string
}> = ({ primaryDate, secondaryDate, template }) => {
	if (!isGrowth(template)) return null
	return (
		<FormattedMessage
			id={GROWTH_DATES_ID}
			values={{
				primaryDate,
				secondaryDate,
				b: (text: string) => <b>{text}</b>,
			}}
		/>
	)
}

const Tooltip: React.FC<TooltipProps> = ({
	active,
	payload,
	template,
	primaryMetric,
	secondaryMetric,
}) => {
	if (!active || !payload?.length) return null
	const intl = useIntl()
	return (
		<div className="benchmark-timeline-tooltip">
			<GrowthDates
				template={template}
				primaryDate={payload[0]?.payload.primaryDate}
				secondaryDate={payload[0]?.payload.secondaryDate}
			/>
			{payload?.map((item) => {
				const date: string = item.payload[mapDates[item.dataKey]]
				const detail: number = item.payload[mapGrowth[item.dataKey]]
				const legendTrack = getLegend(date, template, item.dataKey)
				const detailTrack = getDetailTrack(detail, template, item.dataKey)
				const symbol = getSymbol(
					template,
					primaryMetric,
					secondaryMetric,
					item.dataKey
				)
				return (
					<div key={item.name} className="label">
						<ColorLine
							color={item.color}
							strokeDasharray={item.strokeDasharray ? "4, 2" : undefined}
						/>
						<FormattedMessage
							id={METRIC_ID}
							values={{
								legendTrack,
								symbol,
								amount: formatNumber(item.value, intl),
								b: (text: string) => <b>{text}</b>,
							}}
						/>
						<FormattedPercentage
							detail={detailTrack}
							symbol={symbol}
							intl={intl}
						/>
					</div>
				)
			})}
		</div>
	)
}

export default Tooltip
